import { Component, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { saveAs } from 'file-saver'
import { debounceTime, tap } from 'rxjs/operators'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { BehaviorSubject, Observable } from 'rxjs'

import { AuthenticationService } from '@/services/authentication.service'
import { SettlementsService } from '@/services/settlements.service'

import { Merchant } from '@/domain/merchant'
import { Settlement } from '@/domain/settlement'

@UntilDestroy()
@Component({
  selector: 'settlements-view',
  templateUrl: './settlements-view.component.html',
})
export class SettlementsViewComponent implements OnInit {

  merchant: Merchant
  months: number[] = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 ]
  today: Date = new Date()
  unsettledAsOf: NgbDateStruct = this.asNgbDateStruct(new Date())
  // Which report download is in flight ('unsettled' | 'summary' | null): the yearly summary
  // takes ~15-30 s server side, so the button shows a spinner and is disabled meanwhile.
  downloading: string = null

  // Pagination properties
  page: number = 1
  pageSize: number = 15 // Make sure this matches a value in the radio group
  totalItems: number = 0
  totalPages: number = 0
  Math = Math // Expose Math to template

  // Observable for current page settlements
  private _currentPageSettlements = new BehaviorSubject<Settlement[]>([]);
  currentPageSettlements$: Observable<Settlement[]>;

  // Search filter
  filter = new FormControl('');
  currentSearchTerm: string = '';

  // Loading state
  loading: boolean = true;

  constructor(
    public auth: AuthenticationService,
    public service: SettlementsService
  ) {
    const user = this.auth.currentUserValue
    if (!user?.merchant) {
      // User not logged in or no merchant - this shouldn't happen on this route
      // but handle gracefully
      this.merchant = null
    } else {
      this.merchant = user.merchant
    }
    this.currentPageSettlements$ = this._currentPageSettlements.asObservable();
  }

  ngOnInit(): void {
    this.getSettlementsPaginated();
    this.initializeFiltering();
  }

  pageChange(page: number): void {
    this.page = page;
    this.getSettlementsPaginated();
  }

  pageSizeChange(pageSize: number): void {
    // Make sure pageSize is a number
    this.pageSize = typeof pageSize === 'string' ? parseInt(pageSize, 15) : pageSize
    this.page = 1 // Reset to first page when changing page size
    this.getSettlementsPaginated()
  }

  clearFilter() {
    this.filter.setValue('');
    this.currentSearchTerm = '';
  }

  private getSettlementsPaginated(): void {
    this.loading = true;
    this.service.getSettlementsPaginated(this.page, this.pageSize, this.currentSearchTerm).subscribe(
      (result) => {
        this.totalItems = result.total;
        this.page = result.page;
        this.totalPages = result.totalPages;
        // Update the current page settlements only - this is key for correct display
        this._currentPageSettlements.next(result.items);
        this.loading = false;
      },
      error => {
        this.loading = false;
      }
    );
  }

  private initializeFiltering() {
    // Server-side search implementation
    this.filter.valueChanges.pipe(
      debounceTime(400), // Wait for 400ms pause in events
      tap(term => {
        this.currentSearchTerm = term;
        this.page = 1; // Reset to first page on new search
        this.getSettlementsPaginated(); // Trigger server-side search
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  hasPdf(settlement: Settlement): boolean {
    return !!settlement.pdf_document && !!settlement.pdf_document.uuid
  }

  pdfPath(settlement: Settlement): string {
    return `/s/${settlement.pdf_document.uuid}`
  }

  getPastSettlementReport(year: number, month: number): void {
    this.service.getPastSettlementReport(year, month).subscribe(
      blob => {
        saveAs(blob, `tilitysraportti-${year}-${month}.pdf`)
      }
    )
  }

  getSettlementReport(prev: boolean = false): void {
    this.service.getSettlementReport(prev).subscribe(
      blob => {
        saveAs(blob, `tilitysraportti.pdf`)
      }
    )
  }

  getUnsettledReportToday(): void {
    this.getUnsettledReportForDate(this.today)
  }

  getUnsettledReportForDate(date: Date): void {
    let on = this.formatDate(date)
    this.downloading = 'unsettled'
    this.service.getUnsettledReport(on).subscribe(
      blob => {
        this.downloading = null
        saveAs(blob, `tilittamattomat-${on}.xlsx`)
      },
      error => { this.downloading = null }
    )
  }

  getUnsettledReportForSelectedDate(): void {
    let on = this.parseDateInput(this.unsettledAsOf)
    if (!on) {
      return
    }
    this.getUnsettledReportForDate(on)
  }

  // ngbDatepicker hands back an NgbDateStruct when a day is picked or the typed text parses;
  // when the typed text does not parse it leaves the raw string in the model. Accept both
  // yyyy-mm-dd and the Finnish d.m.yyyy; null means "not a date".
  private parseDateInput(value: NgbDateStruct | string | null): Date | null {
    if (!value) {
      return null
    }
    let y: number, m: number, d: number
    if (typeof value === 'string') {
      let iso = value.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
      let fi = value.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
      if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3] }
      else if (fi) { y = +fi[3]; m = +fi[2]; d = +fi[1] }
      else { return null }
    } else {
      y = value.year; m = value.month; d = value.day
    }
    if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) {
      return null
    }
    return new Date(y, m - 1, d)
  }

  getSummaryReport(year: number): void {
    this.downloading = 'summary'
    this.service.getSummaryReport(year).subscribe(
      blob => {
        this.downloading = null
        saveAs(blob, `yhteenveto-${year}.xlsx`)
      },
      error => { this.downloading = null }
    )
  }

  years(): number[] {
    if (!this.merchant || !this.merchant.hasSettlements()) {
      return []
    }
    let startYear = this.merchant.earliest_settlement_date.year()
    let currentYear = this.today.getFullYear()
    let years = []
    for (var i = currentYear; i >= startYear; i--) {
      years.push(i)
    }
    return years
  }

  show(year: number, month: number): boolean {
    if (!this.merchant) {
      return false
    }
    let earliestDate = this.merchant.earliest_settlement_date
    if (earliestDate == null) {
      return false
    }
    if (year < earliestDate.year() || year > this.today.getFullYear()) {
      return false
    }
    if (year == earliestDate.year()) {
      return month >= earliestDate.month().value() && (earliestDate.year() != this.today.getFullYear() || month <= (this.today.getMonth() + 1))
    }
    if (year > earliestDate.year() && year < this.today.getFullYear()) {
      return true
    }
    return month <= (this.today.getMonth() + 1)
  }

  zeroPad2(n: number): string {
    return n ? (""+n).padStart(2, '0') : ''
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${(""+(date.getMonth()+1)).padStart(2, '0')}-${(""+date.getDate()).padStart(2, '0')}`
  }

  private asNgbDateStruct(date: Date): NgbDateStruct {
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() }
  }
}
